import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { useAuth } from '../../context/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import logo from '../../images/itclarifies_logo_v2.svg';

const navLinks = [
  { label: 'How it works', href: '/#how-it-works' },
  { label: 'For organizations', href: '/#for-organizations' },
  { label: 'Pricing', href: '/#pricing' },
];

export default function HomeHeader() {
  const { loginWithRedirect } = useAuth();
  const [scrolled, setScrolled] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogin = () => {
    const hasConsent = localStorage.getItem('has_consented');
    loginWithRedirect({
      authorizationParams: {
        ...(hasConsent ? {} : { prompt: 'consent' }),
        scope: 'openid profile email offline_access',
        audience: process.env.REACT_APP_AUDIENCE,
      },
    });

    if (!hasConsent) {
      localStorage.setItem('has_consented', 'true');
    }
  };

  const linkSx = {
    color: 'text.primary',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: { xs: '0.875rem', sm: '0.95rem' },
    px: { xs: 1, sm: 1.25 },
    minWidth: 0,
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.96)' : 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: scrolled ? 'divider' : 'transparent',
          backdropFilter: scrolled ? 'saturate(120%) blur(8px)' : 'none',
          transition: 'border-color 200ms ease, background-color 200ms ease',
        }}
      >
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, sm: 72 } }}>
          <Container maxWidth="lg" sx={{ px: 'var(--page-x)' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="Itclarifies"
                  sx={{ height: 34, width: 'auto', display: 'block' }}
                />
                <Typography
                  variant="body1"
                  component="span"
                  sx={{
                    fontSize: { xs: '1.15rem', sm: '1.35rem' },
                    fontWeight: 700,
                    ml: 1,
                    display: { xs: 'none', sm: 'block' },
                    letterSpacing: '-0.02em',
                  }}
                >
                  itclarifies
                </Typography>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }} />

              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                {navLinks.map((item) => (
                  <Button
                    key={item.href}
                    component="a"
                    href={item.href}
                    sx={linkSx}
                  >
                    {item.label}
                  </Button>
                ))}
                
                <Button
                  onClick={handleLogin}
                  variant="outlined"
                  color="primary"
                  sx={{ ml: 1 }}
                >
                  Log in
                </Button>
              </Stack>

              <IconButton
                edge="end"
                aria-label="Open navigation menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          </Container>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ width: 280, pt: 2 }} role="presentation">
          <Typography variant="subtitle1" sx={{ px: 2, pb: 1, fontWeight: 700 }}>
            itclarifies
          </Typography>
          <Divider />
          <List>
            {navLinks.map((item) => (
              <ListItem key={item.href} disablePadding>
                <ListItemButton
                  component="a"
                  href={item.href}
                  onClick={() => setDrawerOpen(false)}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Button variant="outlined" onClick={handleLogin} fullWidth>
              Log in
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Offset for fixed header on pages that import this bar */}
      <Toolbar sx={{ minHeight: { xs: 64, sm: 72 } }} />
    </Box>
  );
}
